import React, { useState, useEffect } from 'react';
import {
  HardHat, Users, Clock, TrendingDown, BarChart3,
  ArrowUpRight, ArrowDownRight, CheckCircle2, AlertTriangle,
  FileText, Brain, ChevronRight, CloudRain, Calendar
} from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { obrasApi, contactsApi } from '@/core/adapter-api';
import { useNavigate } from 'react-router-dom';
import { fetchWeather, getWeatherInfo, isRainyWeather, DEFAULT_COORDS, type WeatherData } from '../services/weatherService';
import { fetchHolidays, getUpcomingHolidays, formatHolidayDate, type PublicHoliday } from '../services/holidayService';

// ─── Progress Ring SVG ────────────────────────────────────────────────────────

const ProgressRing: React.FC<{ value: number; size?: number; color?: string; label: string }> = ({
  value, size = 80, color = '#f59e0b', label
}) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={6} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-lg font-bold text-white">{value}%</span>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold text-center">{label}</span>
    </div>
  );
};

// ─── Dashboard Page ───────────────────────────────────────────────────────────

export const BuildProDashboard: React.FC = () => {
  const navigate = useNavigate();
  const rawObras = useQuery(() => obrasApi.all(), [], ['obras']) || [];
  const rawContacts = useQuery(() => contactsApi.all(), [], ['contacts']) || [];

  const allTasks = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('obra_tasks');
  }, [], ['obra_tasks']) || [];

  const allTimeLogs = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('time_logs');
  }, [], ['time_logs']) || [];

  // ── Weather & Holidays (free APIs) ──
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState<PublicHoliday[]>([]);

  useEffect(() => {
    fetchWeather(DEFAULT_COORDS.lat, DEFAULT_COORDS.lng).then(w => { if (w) setWeather(w); });
    const year = new Date().getFullYear();
    fetchHolidays(year).then(h => setUpcomingHolidays(getUpcomingHolidays(h, 14)));
  }, []);

  // ── Stats ──
  const activeObras = rawObras.filter(o => o.status === 'en_obra').length;
  const totalBudget = rawObras.reduce((s, o) => s + o.budget, 0);
  const workers = rawContacts.filter(c => c.type === 'supplier' || (c as any).role).length;
  const tasksTotal = allTasks.length;
  const tasksDone = allTasks.filter(t => t.status === 'done').length;
  const avgProgress = rawObras.length > 0
    ? Math.round(rawObras.reduce((s, o) => {
        const obraTasks = allTasks.filter(t => t.obraId === o.id);
        const done = obraTasks.filter(t => t.status === 'done').length;
        return s + (obraTasks.length > 0 ? (done / obraTasks.length) * 100 : 0);
      }, 0) / rawObras.length)
    : 0;

  const todayLogs = allTimeLogs.filter(l => {
    const d = new Date(l.startTime);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  });

  const formatK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}k€` : `${n}€`;

  const quickStats = [
    { icon: HardHat, label: 'Obras activas', value: String(activeObras), color: 'from-amber-500 to-orange-600', trend: rawObras.length > 0 ? '+' : '', path: '/obras' },
    { icon: Users, label: 'Equipo', value: String(workers), color: 'from-blue-500 to-indigo-600', trend: '', path: '/equipo' },
    { icon: Clock, label: 'Fichajes hoy', value: String(todayLogs.length), color: 'from-emerald-500 to-teal-600', trend: '', path: '/control-horario' },
    { icon: TrendingDown, label: 'Presupuesto total', value: formatK(totalBudget), color: 'from-purple-500 to-pink-600', trend: '', path: '/costes-reales' },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-950/40 via-[#0d1117] to-[#0d1117] border border-amber-500/10 p-6 md:p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <HardHat size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-100">
                {(() => { const h = new Date().getHours(); return h < 14 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches'; })()}
              </h1>
              <p className="text-sm text-gray-500">
                {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} · {rawObras.length} obras registradas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Weather + Holidays Bar (Free APIs) ── */}
      {(weather || upcomingHolidays.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Weather Widget */}
          {weather && (
            <div className={`relative overflow-hidden rounded-xl border p-4 ${isRainyWeather(weather.weatherCode) ? 'bg-blue-950/30 border-blue-500/20' : 'bg-[#0d1117] border-white/8'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getWeatherInfo(weather.weatherCode).icon}</span>
                  <div>
                    <p className="text-2xl font-bold text-gray-100">{Math.round(weather.temperature)}°C</p>
                    <p className="text-xs text-gray-400">{getWeatherInfo(weather.weatherCode).label}</p>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <p className="text-xs text-gray-500">💨 {weather.windSpeed} km/h</p>
                  <p className="text-xs text-gray-500">💧 {weather.humidity}%</p>
                  {weather.precipitation > 0 && (
                    <p className="text-xs text-blue-400 font-semibold">🌧️ {weather.precipitation} mm</p>
                  )}
                </div>
              </div>
              {/* 7-day mini forecast */}
              <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 overflow-x-auto">
                {weather.daily.slice(1).map((d, i) => {
                  const dayName = new Date(d.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short' });
                  return (
                    <div key={i} className="flex flex-col items-center min-w-[48px] text-center">
                      <span className="text-[10px] text-gray-500 uppercase">{dayName}</span>
                      <span className="text-sm">{getWeatherInfo(d.weatherCode).icon}</span>
                      <span className="text-[10px] text-gray-300 font-bold">{Math.round(d.tempMax)}°</span>
                      <span className="text-[10px] text-gray-600">{Math.round(d.tempMin)}°</span>
                      {d.precipitationProbability > 30 && (
                        <span className="text-[9px] text-blue-400">{d.precipitationProbability}%💧</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {isRainyWeather(weather.weatherCode) && (
                <div className="mt-2 flex items-center gap-2 text-xs text-blue-300 bg-blue-500/10 rounded-lg px-3 py-1.5">
                  <CloudRain size={14} /> <span className="font-semibold">⚠️ Lluvia detectada — posible impacto en obras</span>
                </div>
              )}
            </div>
          )}

          {/* Holiday Alerts */}
          {upcomingHolidays.length > 0 && (
            <div className="bg-[#0d1117] border border-amber-500/15 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-amber-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400">Próximos festivos</h3>
              </div>
              <div className="space-y-2">
                {upcomingHolidays.slice(0, 3).map((h, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-500/5 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm text-gray-200 font-medium">{h.localName}</p>
                      <p className="text-[10px] text-gray-500">{formatHolidayDate(h.date)}</p>
                    </div>
                    <span className="text-xs text-amber-400 font-bold bg-amber-500/10 px-2 py-0.5 rounded">
                      {Math.ceil((new Date(h.date + 'T00:00:00').getTime() - Date.now()) / 86400000)} días
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat, i) => (
          <button
            key={i}
            onClick={() => navigate(stat.path)}
            className="group relative bg-[#0d1117] border border-white/8 rounded-xl p-5 text-left hover:border-white/15 transition-all hover:-translate-y-0.5 active:translate-y-0"
          >
            <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3 shadow-lg`}>
              <stat.icon size={18} className="text-white" />
            </div>
            <p className="text-2xl font-bold text-gray-100">{stat.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-0.5">{stat.label}</p>
            <ChevronRight size={14} className="absolute top-5 right-4 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>

      {/* Middle Row: Progress + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Progress Overview */}
        <div className="lg:col-span-1 bg-[#0d1117] border border-white/8 rounded-xl p-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-6">Avance General</h3>
          <div className="flex justify-center gap-6">
            <div className="relative">
              <ProgressRing value={avgProgress} size={90} color="#f59e0b" label="Avance medio" />
            </div>
            <div className="relative">
              <ProgressRing value={tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0} size={90} color="#10b981" label="Tareas hechas" />
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/5 grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-gray-200">{tasksDone}/{tasksTotal}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tareas Completadas</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-200">{rawObras.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Total Obras</p>
            </div>
          </div>
        </div>

        {/* Obras List */}
        <div className="lg:col-span-2 bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Obras en curso</h3>
            <button onClick={() => navigate('/obras')} className="text-[10px] text-amber-500 font-bold uppercase hover:text-amber-400 transition-colors">
              Ver todas →
            </button>
          </div>
          {rawObras.length > 0 ? (
            <div className="divide-y divide-white/5">
              {rawObras.slice(0, 5).map(obra => {
                const obraTasks = allTasks.filter(t => t.obraId === obra.id);
                const done = obraTasks.filter(t => t.status === 'done').length;
                const progress = obraTasks.length > 0 ? Math.round((done / obraTasks.length) * 100) : 0;
                const statusColor = obra.status === 'terminada' ? 'bg-emerald-500' : obra.status === 'en_obra' ? 'bg-amber-500' : 'bg-blue-500';

                return (
                  <button
                    key={obra.id}
                    onClick={() => navigate('/obras')}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/3 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-2 h-2 rounded-full ${statusColor} flex-shrink-0`} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-200 font-medium truncate">{obra.name}</p>
                        <p className="text-xs text-gray-500">{obra.location || 'Sin ubicación'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:flex items-center gap-2 w-28">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${progress >= 100 ? 'bg-emerald-500' : progress >= 50 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-500 w-8 text-right">{progress}%</span>
                      </div>
                      <span className="text-sm font-bold text-amber-400 w-16 text-right">{formatK(obra.budget)}</span>
                      <ChevronRight size={14} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-5 py-12 text-center">
              <HardHat size={32} className="mx-auto text-gray-600 mb-3" />
              <p className="text-sm text-gray-400 font-medium">No hay obras todavía</p>
              <button onClick={() => navigate('/obras')} className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-500 transition-colors">
                Crear primera obra
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: BarChart3, label: 'Seguimiento', desc: 'Control de avance', path: '/seguimiento', color: 'text-amber-400' },
          { icon: TrendingDown, label: 'Costes Reales', desc: 'Análisis financiero', path: '/costes-reales', color: 'text-emerald-400' },
          { icon: Brain, label: 'Presupuestos IA', desc: 'Generar con IA', path: '/presupuestos-ia', color: 'text-purple-400' },
          { icon: FileText, label: 'Importar BC3', desc: 'Archivos FIEBDC', path: '/mediciones', color: 'text-blue-400' },
          { icon: BarChart3, label: 'Optimización VE', desc: 'Ingeniería de valor', path: '/optimizacion-ve', color: 'text-emerald-400' },
        ].map((action, i) => (
          <button
            key={i}
            onClick={() => navigate(action.path)}
            className="group bg-[#0d1117] border border-white/8 rounded-xl p-4 text-left hover:border-white/15 transition-all hover:-translate-y-0.5"
          >
            <action.icon size={20} className={`${action.color} mb-2 group-hover:scale-110 transition-transform`} />
            <p className="text-sm font-semibold text-gray-200">{action.label}</p>
            <p className="text-[10px] text-gray-500">{action.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default BuildProDashboard;
